import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { BiTrash, BiDoorOpen } from 'react-icons/bi'
import { IoIosAdd } from 'react-icons/io'
import { getRoomTypesList, deleteRoomType } from '../../actions/roomTypes'
import Button from '../../components/Button'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import RoomTypeModal from './Modal'
import styles from './styles.module.scss'

const TABLE_HEADERS = [
  { key: 'number', label: 'Номер', width: '100px' },
  { key: 'name', label: 'Название' },
  { key: 'scheduling', label: 'Расписание', width: '120px' },
  { key: 'actions', label: '', width: '60px' }
]

const RoomTypes = () => {
  const dispatch = useDispatch()
  const { list, loading } = useSelector(state => state.roomTypes)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortField, setSortField] = useState('name')
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    dispatch(getRoomTypesList())
  }, [dispatch])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.rtid, name: item.name })
  }, [])

  const openModal = useCallback((item) => () => {
    setSelectedItem(item || {})
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deleteRoomType(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const handleSort = useCallback((field) => {
    if (sortField === field) setSortAsc(v => !v)
    else { setSortField(field); setSortAsc(true) }
  }, [sortField])

  const sortedList = useMemo(() => {
    if (!list) return []
    return [...list].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortAsc ? aVal - bVal : bVal - aVal
      return sortAsc
        ? String(aVal).localeCompare(String(bVal), 'ru')
        : String(bVal).localeCompare(String(aVal), 'ru')
    })
  }, [list, sortField, sortAsc])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem && !selectedItem.rtid

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <BiDoorOpen size={64} className={styles.emptyIcon} />
      <p>Нет добавленных типов кабинетов</p>
      <Button onClick={openModal()}>Добавить первый тип</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка типов кабинетов...</span>
    </div>
  )

  const renderTable = () => (
    <div className={styles.tableContainer}>
      <Table className={styles.table}>
        <thead>
          <tr>
            {TABLE_HEADERS.map(({ key, label, width }) => (
              <th
                key={key}
                style={{ ...(width ? { width } : {}), ...(key !== 'actions' ? { cursor: 'pointer' } : {}) }}
                onClick={key !== 'actions' ? () => handleSort(key) : undefined}
              >{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedList.map((item) => (
            <tr
              key={item.rtid}
              onClick={openModal(item)}
              className={styles.tableRow}
            >
              <td>
                {item.number
                  ? <span className={styles.itemNumber}>{item.number}</span>
                  : <span className={styles.dash}>—</span>}
              </td>
              <td className={styles.nameCell}>
                <span className={styles.itemName}>{item.name}</span>
              </td>
              <td>
                {item.scheduling ? 'Да' : 'Нет'}
              </td>
              <td>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleDelete(item)}
                  title={`Удалить тип кабинета ${item.name}`}
                  aria-label={`Удалить тип кабинета ${item.name}`}
                >
                  <BiTrash size={20} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className={styles.actions}>
        <Button style={{ padding: '.5rem 1.5rem' }} onClick={openModal()}>
          <IoIosAdd size={30} />
          Добавить
        </Button>
        <span className={styles.counter}>
          Всего записей: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Типы кабинетов</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderTable()}
      </main>

      <RoomTypeModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedItem}
        addMode={isAddMode}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить тип кабинета ${confirmation.name}?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}

export default RoomTypes
